<template>
    <div>
        <modal 
            :show.sync="localOpen"
            class="report-predict-download-modal"
        >
            <template slot="header">
                <h3 class="title title-up">Download Reports For Campaign #{{ campaignId }}</h3>
            </template>

            <!-- Loading State -->
            <div v-if="isLoading" class="loading-container">
                <i class="fas fa-spinner fa-spin" style="font-size: 30px;"></i>
                <p>Loading reports...</p>
            </div>

            <!-- Reports List -->
            <div v-else class="reports-container">
                <!-- Custom fields for reports (campaign-level config) -->
                <div class="campaign-fields-section">
                    <div class="campaign-fields-header">
                        <span class="campaign-fields-title">Custom fields for reports</span>
                        <el-button
                            v-if="!editFieldConfig"
                            type="text"
                            size="small"
                            class="campaign-fields-change-btn"
                            @click="openEditFieldConfig"
                        >
                            Change
                        </el-button>
                    </div>
                    <p v-if="!editFieldConfig" class="campaign-fields-subtitle">Applied to the next report fetch.</p>
                    <div v-if="!editFieldConfig" class="campaign-fields-status">
                        <span v-if="campaignFieldConfig">{{ campaignFieldConfig.display_name || 'Custom' }}</span>
                        <span v-else class="campaign-fields-none">No custom fields set. Next fetch will use default.</span>
                    </div>
                    <div v-else class="campaign-fields-edit">
                        <div class="campaign-fields-edit-row">
                            <label class="campaign-fields-label">Configuration</label>
                            <el-select
                                v-model="editFieldConfigValue"
                                placeholder="Select or create custom configuration"
                                size="small"
                                class="campaign-fields-select"
                                @change="onEditFieldConfigSelectChange"
                            >
                                <el-option
                                    :value="EDIT_FIELD_CONFIG_NO_CUSTOM_VALUE"
                                    :label="'No custom fields'"
                                />
                                <el-option
                                    v-for="item in customFieldsListFromGeneral"
                                    :key="item.id"
                                    :label="item.field_name || `Config #${item.id}`"
                                    :value="item.id"
                                />
                                <el-option
                                    :value="EDIT_FIELD_CONFIG_CUSTOM_VALUE"
                                    :label="'➕ Create custom configuration…'"
                                />
                            </el-select>
                        </div>
                        <template v-if="editFieldConfigValue === EDIT_FIELD_CONFIG_CUSTOM_VALUE">
                            <div class="campaign-fields-edit-row">
                                <label class="campaign-fields-label">Configuration name <span class="required">*</span></label>
                                <el-input
                                    v-model="editFieldConfigName"
                                    placeholder="e.g. My Campaign Fields"
                                    size="small"
                                    maxlength="255"
                                    show-word-limit
                                    class="campaign-fields-name-input"
                                />
                            </div>
                            <div class="campaign-fields-edit-row">
                                <label class="campaign-fields-label">Select custom fields</label>
                                <div class="campaign-fields-master-list">
                                    <label
                                        v-for="f in masterFieldsForCampaign"
                                        :key="f.id"
                                        class="campaign-fields-master-item"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="f.id"
                                            v-model="editFieldConfigSelectedIds"
                                            @change="updateEditFieldConfigOrder"
                                        />
                                        <span>{{ f.field_name }}</span>
                                    </label>
                                </div>
                            </div>
                            <div class="campaign-fields-edit-row">
                                <label class="campaign-fields-label">Selected order</label>
                                <div v-if="editFieldConfigSelectedIds.length === 0" class="campaign-fields-order-empty">No fields selected.</div>
                                <div
                                    v-else
                                    class="campaign-fields-order-list"
                                    @dragover.prevent
                                    @drop="handleEditFieldConfigDrop"
                                >
                                    <div
                                        v-for="(fid, idx) in editFieldConfigSelectedIds"
                                        :key="fid"
                                        class="campaign-fields-order-item-inline"
                                        :class="{
                                            'is-dragging': editFieldConfigDraggedIndex === idx,
                                            'is-drag-over': editFieldConfigDragOverIndex === idx && editFieldConfigDraggedIndex !== idx
                                        }"
                                        draggable="true"
                                        @dragstart="handleEditFieldConfigDragStart(idx, $event)"
                                        @dragend="handleEditFieldConfigDragEnd"
                                        @dragover.prevent="handleEditFieldConfigDragOver(idx)"
                                        @dragleave="handleEditFieldConfigDragLeave"
                                    >
                                        <span class="order-label-inline">
                                            <i class="el-icon-rank drag-handle-inline"></i>
                                            {{ idx + 1 }}. {{ getMasterFieldNameById(fid) }}
                                        </span>
                                        <div class="order-actions-inline">
                                            <el-button size="mini" :disabled="idx === 0" @click="moveEditFieldConfig(fid, 'up')">Up</el-button>
                                            <el-button size="mini" :disabled="idx === editFieldConfigSelectedIds.length - 1" @click="moveEditFieldConfig(fid, 'down')">Down</el-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="editFieldConfigError" class="campaign-fields-error">{{ editFieldConfigError }}</div>
                        </template>
                        <div class="campaign-fields-edit-actions">
                            <el-button size="small" @click="cancelEditFieldConfig">Cancel</el-button>
                            <el-button type="primary" size="small" :loading="isSavingFieldConfig" @click="saveEditFieldConfig">Save</el-button>
                        </div>
                    </div>
                </div>
                <!-- /Custom fields for reports -->

                <div class="reports-header">
                    <p class="header-text">Choose a report to download</p>
                    <el-button
                        v-if="false"
                        size="small"
                        class="download-all-header-btn"
                        @click="openDownloadAllModal"
                    >
                        Download Bundle
                    </el-button>
                </div>

                <div class="reports-list">
                    <!-- Empty State Inside List -->
                    <div v-if="reports.length === 0" class="no-data-container">
                        <p>No report history available.</p>
                    </div>

                    <!-- Report Items -->
                    <div
                        v-else
                        v-for="(report, index) in reports"
                        :key="index"
                        class="report-item"
                        :class="{ 'is-processing': isProcessingOrPending(report.job_status) }"
                    >
                        <div class="report-info">
                            <div class="report-title">
                                <i class="el-icon-document"></i>
                                <span>{{ formatReportTitle(report) }}</span>
                                <span
                                    v-if="report.job_status"
                                    class="report-status"
                                    :class="`status-${getDisplayStatus(report.job_status)}`"
                                >
                                    {{ getDisplayStatus(report.job_status) }}
                                </span>
                            </div>
                            <div class="report-dates">
                                <span class="date-label">
                                    <i class="el-icon-date"></i>
                                    {{ formatDate(report.start_date) }} - {{ formatDate(report.end_date) }}
                                </span>
                                <span v-if="report.total_leads !== undefined && report.total_leads !== null" class="total-leads-label">
                                    <i class="el-icon-user"></i>
                                    {{ formatNumber(report.total_leads) }} leads
                                </span>
                            </div>
                            <div v-if="isProcessingOrPending(report.job_status)" class="report-progress">
                                <span class="progress-label">Processing</span>
                                <div class="progress-track">
                                    <div
                                        class="progress-fill"
                                        :style="{ width: `${getProgressPercentage(report)}%` }"
                                    ></div>
                                </div>
                                <span class="progress-percent">{{ getProgressPercentage(report) }}%</span>
                            </div>
                        </div>

                        <div class="report-actions">
                            <el-button
                                type="text"
                                class="btn-custom-fields"
                                :disabled="isProcessingOrPending(report.job_status)"
                                @click="openCustomFieldsModal(report)"
                            >
                                Custom Fields
                            </el-button>
                            <el-dropdown
                                trigger="click"
                                placement="bottom-end"
                                :disabled="isProcessingOrPending(report.job_status)"
                                @command="(type) => handleDownloadType(report, type)"
                            >
                                <el-button
                                    type="text"
                                    icon="el-icon-download"
                                    class="btn-download-trigger"
                                    :title="`Download ${formatReportTitle(report)}`"
                                    :disabled="isProcessingOrPending(report.job_status)"
                                ></el-button>
                                <el-dropdown-menu slot="dropdown">
                                    <el-dropdown-item
                                        v-for="csv in csvTypesList"
                                        :key="csv.csv_type"
                                        :command="csv.csv_type"
                                    >{{ csv.name }}</el-dropdown-item>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </div>
                    </div>
                </div>
            </div>
        </modal>

        <!-- Custom Fields Modal -->
        <modal 
            :show.sync="customFieldsModalOpen"
            class="custom-fields-modal"
        >
            <template slot="header">
                <h3 class="title title-up">Custom Fields - {{ currentCustomReport ? formatReportTitle(currentCustomReport) : '' }}</h3>
            </template>

            <div class="custom-fields-modal-content">
                <el-tabs v-model="activeTab" type="border-card" @tab-click="onCustomFieldsTabClick">
                    <el-tab-pane label="Configure" name="configure">
                        <div class="custom-fields-tab-content">
                            <div class="custom-fields-section">
                                <div class="custom-fields-title">Select custom fields</div>
                                <div class="custom-fields-list">
                                    <label
                                        v-for="field in customFields"
                                        :key="field.key"
                                        class="custom-field-item"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="field.key"
                                            v-model="currentCustomFieldSelections"
                                        />
                                        <span>{{ field.label }}</span>
                                    </label>
                                </div>
                            </div>

                            <el-divider></el-divider>

                            <div class="custom-fields-section custom-fields-order-section">
                                <div class="custom-fields-order-header">
                                    <div class="custom-fields-order-title">Selected order</div>
                                    <div class="custom-fields-name-input">
                                        <label class="name-input-label">Report Name:</label>
                                        <el-input
                                            v-model="customFieldsReportName"
                                            placeholder="Enter report name (optional)"
                                            size="small"
                                            style="width: 250px;"
                                        ></el-input>
                                    </div>
                                </div>
                                <div v-if="currentCustomFieldSelections.length === 0" class="custom-fields-empty">
                                    No fields selected.
                                </div>
                                <div
                                    v-else
                                    class="custom-fields-order-list"
                                    @dragover.prevent
                                    @drop="handleDrop"
                                >
                                    <div
                                        v-for="(fieldKey, index) in currentCustomFieldSelections"
                                        :key="fieldKey"
                                        class="custom-fields-order-item"
                                        :class="{
                                            'is-dragging': draggedIndex === index,
                                            'is-drag-over': dragOverIndex === index && draggedIndex !== index
                                        }"
                                        draggable="true"
                                        @dragstart="handleDragStart(index, $event)"
                                        @dragend="handleDragEnd"
                                        @dragover.prevent="handleDragOver(index)"
                                        @dragleave="handleDragLeave"
                                    >
                                        <span class="order-label">
                                            <i class="el-icon-rank drag-handle"></i>
                                            {{ index + 1 }}. {{ getFieldLabel(fieldKey) }}
                                        </span>
                                        <div class="order-actions">
                                            <el-button
                                                size="mini"
                                                :disabled="index === 0"
                                                @click="moveCustomFieldInModal(fieldKey, 'up')"
                                            >
                                                Up
                                            </el-button>
                                            <el-button
                                                size="mini"
                                                :disabled="index === currentCustomFieldSelections.length - 1"
                                                @click="moveCustomFieldInModal(fieldKey, 'down')"
                                            >
                                                Down
                                            </el-button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="custom-fields-actions">
                                <div class="custom-fields-action-buttons">
                                    <el-button @click="selectAllCustomFieldsInModal">Select All</el-button>
                                    <el-button @click="clearCustomFieldsInModal">Clear</el-button>
                                    <el-button
                                        type="primary"
                                        @click="downloadCustomFieldsFromModal"
                                    >
                                        Download Custom CSV
                                    </el-button>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <el-tab-pane label="History" name="history">
                        <div class="custom-fields-tab-content">
                            <div class="custom-fields-history-section">
                                <div v-if="customFieldsHistoryLoading" class="custom-fields-history-loading">
                                    <i class="el-icon-loading"></i>
                                    <span>Loading history...</span>
                                </div>
                                <div v-else-if="filteredCustomFieldsHistory.length === 0" class="custom-fields-empty">
                                    No download history available.
                                </div>
                                <div v-else class="custom-fields-history-list">
                                    <div
                                        v-for="(history, index) in filteredCustomFieldsHistory"
                                        :key="index"
                                        class="custom-fields-history-item"
                                    >
                                        <div class="history-header">
                                            <div class="history-info">
                                                <div class="history-main-info">
                                                    <span class="history-name" v-if="history.name">{{ history.name }}</span>
                                                    <span
                                                        class="history-status-badge"
                                                        :class="`history-status-${history.status || 'completed'}`"
                                                    >
                                                        {{ history.status === 'processing' ? 'Processing' : 'Completed' }}
                                                    </span>
                                                    <span class="history-date">{{ formatHistoryDate(history.downloadedAt) }}</span>
                                                    <span class="history-fields-count">{{ history.fields.length }} fields</span>
                                                </div>
                                                <div v-if="history.status === 'processing'" class="history-progress">
                                                    <span class="history-progress-label">Processing</span>
                                                    <div class="history-progress-bar">
                                                        <div
                                                            class="history-progress-fill"
                                                            :style="{ width: `${history.progress || 0}%` }"
                                                        ></div>
                                                    </div>
                                                    <span class="history-progress-percent">{{ history.progress || 0 }}%</span>
                                                </div>
                                            </div>
                                            <div class="history-actions">
                                                <el-button
                                                    type="text"
                                                    icon="el-icon-download"
                                                    class="history-download-btn"
                                                    :title="history.status === 'processing' ? 'Processing...' : 'Download'"
                                                    :disabled="history.status === 'processing'"
                                                    @click="loadAndDownloadFromHistory(history)"
                                                ></el-button>
                                            </div>
                                        </div>
                                        <div class="history-expand-section">
                                            <div
                                                class="history-expand-toggle"
                                                @click="toggleHistoryExpand(index)"
                                            >
                                                <i :class="historyExpanded[index] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                                                <span>Column Order</span>
                                            </div>
                                            <div
                                                v-show="historyExpanded[index]"
                                                class="history-fields-order"
                                            >
                                                <div class="history-order-list">
                                                    <div
                                                        v-for="(fieldKey, idx) in history.fields"
                                                        :key="idx"
                                                        class="history-order-item"
                                                    >
                                                        <span class="history-order-number">{{ idx + 1 }}.</span>
                                                        <span class="history-order-label">{{ getFieldLabel(fieldKey) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </modal>

        <!-- Download Bundle Modal -->
        <modal 
            :show.sync="bundleModalOpen"
            class="custom-fields-modal"
        >
            <template slot="header">
                <h3 class="title title-up">Download Bundle For Campaign #{{ campaignId }}</h3>
            </template>

            <div class="custom-fields-modal-content">
                <el-tabs v-model="bundleActiveTab" type="border-card">
                    <el-tab-pane label="Configure" name="configure">
                        <div class="custom-fields-tab-content">
                            <!-- Select Download Type -->
                            <div class="bundle-download-type-section">
                                <div class="custom-fields-title">Select Download Type:</div>
                                <el-radio-group v-model="bundleDownloadType" class="bundle-radio-group">
                                    <el-radio label="csv">CSV Type</el-radio>
                                    <el-radio label="custom">Custom Fields</el-radio>
                                </el-radio-group>
                            </div>

                            <el-divider></el-divider>

                            <!-- CSV Type Selection -->
                            <div v-if="bundleDownloadType === 'csv'" class="bundle-csv-type-section">
                                <div class="custom-fields-title">Select CSV Type:</div>
                                <el-radio-group v-model="bundleCsvType" class="bundle-radio-group">
                                    <el-radio
                                        v-for="csv in csvTypesList"
                                        :key="csv.csv_type"
                                        :label="csv.csv_type"
                                    >{{ csv.name }}</el-radio>
                                </el-radio-group>
                            </div>

                            <!-- Custom Fields Selection -->
                            <div v-if="bundleDownloadType === 'custom'" class="custom-fields-section">
                                <div class="custom-fields-title">Select custom fields</div>
                                <div class="custom-fields-list">
                                    <label
                                        v-for="field in customFields"
                                        :key="field.key"
                                        class="custom-field-item"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="field.key"
                                            v-model="bundleCustomFieldSelections"
                                        />
                                        <span>{{ field.label }}</span>
                                    </label>
                                </div>
                            </div>

                            <el-divider v-if="bundleDownloadType === 'custom'"></el-divider>

                            <!-- Selected Order (only for Custom Fields) -->
                            <div v-if="bundleDownloadType === 'custom'" class="custom-fields-section custom-fields-order-section">
                                <div class="custom-fields-order-header">
                                    <div class="custom-fields-order-title">Selected order</div>
                                    <div class="custom-fields-name-input">
                                        <label class="name-input-label">Report Name:</label>
                                        <el-input
                                            v-model="bundleReportName"
                                            placeholder="Enter report name (optional)"
                                            size="small"
                                            style="width: 250px;"
                                        ></el-input>
                                    </div>
                                </div>
                                <div v-if="bundleCustomFieldSelections.length === 0" class="custom-fields-empty">
                                    No fields selected.
                                </div>
                                <div
                                    v-else
                                    class="custom-fields-order-list"
                                    @dragover.prevent
                                    @drop="handleBundleDrop"
                                >
                                    <div
                                        v-for="(fieldKey, index) in bundleCustomFieldSelections"
                                        :key="fieldKey"
                                        class="custom-fields-order-item"
                                        :class="{
                                            'is-dragging': bundleDraggedIndex === index,
                                            'is-drag-over': bundleDragOverIndex === index && bundleDraggedIndex !== index
                                        }"
                                        draggable="true"
                                        @dragstart="handleBundleDragStart(index, $event)"
                                        @dragend="handleBundleDragEnd"
                                        @dragover.prevent="handleBundleDragOver(index)"
                                        @dragleave="handleBundleDragLeave"
                                    >
                                        <span class="order-label">
                                            <i class="el-icon-rank drag-handle"></i>
                                            {{ index + 1 }}. {{ getFieldLabel(fieldKey) }}
                                        </span>
                                        <div class="order-actions">
                                            <el-button
                                                size="mini"
                                                :disabled="index === 0"
                                                @click="moveBundleField(fieldKey, 'up')"
                                            >
                                                Up
                                            </el-button>
                                            <el-button
                                                size="mini"
                                                :disabled="index === bundleCustomFieldSelections.length - 1"
                                                @click="moveBundleField(fieldKey, 'down')"
                                            >
                                                Down
                                            </el-button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <el-divider></el-divider>

                            <!-- Select Reports -->
                            <div class="bundle-reports-section">
                                <div class="custom-fields-title">Select Reports:</div>
                                <div class="bundle-reports-list">
                                    <div
                                        v-for="report in completedReports"
                                        :key="report.id"
                                        class="bundle-report-item"
                                    >
                                        <el-checkbox
                                            :value="isBundleReportSelected(report)"
                                            @change="(val) => toggleBundleReportSelection(report, val)"
                                        >
                                            <div class="bundle-report-info">
                                                <div class="bundle-report-title">{{ formatReportTitle(report) }}</div>
                                                <div class="bundle-report-details">
                                                    <span class="bundle-report-date">
                                                        <i class="el-icon-date"></i>
                                                        {{ formatDate(report.start_date) }} - {{ formatDate(report.end_date) }}
                                                    </span>
                                                    <span class="bundle-report-leads">
                                                        <i class="el-icon-user"></i>
                                                        {{ formatNumber(report.total_leads) }} leads
                                                    </span>
                                                </div>
                                            </div>
                                        </el-checkbox>
                                    </div>
                                </div>
                            </div>

                            <div class="custom-fields-actions">
                                <div class="custom-fields-action-buttons">
                                    <el-button v-if="bundleDownloadType === 'custom'" @click="selectAllBundleFields">Select All</el-button>
                                    <el-button v-if="bundleDownloadType === 'custom'" @click="clearBundleFields">Clear</el-button>
                                    <el-button
                                        type="primary"
                                        :disabled="selectedBundleReports.length === 0"
                                        @click="handleDownloadBundle"
                                    >
                                        Download Bundle ({{ selectedBundleReports.length }})
                                    </el-button>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <el-tab-pane label="History" name="history">
                        <div class="custom-fields-tab-content">
                            <div class="custom-fields-history-section">
                                <div v-if="filteredBundleDownloadHistory.length === 0" class="custom-fields-empty">
                                    No download history available.
                                </div>
                                <div v-else class="custom-fields-history-list">
                                    <div
                                        v-for="(history, index) in filteredBundleDownloadHistory"
                                        :key="index"
                                        class="custom-fields-history-item"
                                    >
                                        <div class="history-header">
                                            <div class="history-info">
                                                <div class="history-main-info">
                                                    <span class="history-name" v-if="history.name">{{ history.name }}</span>
                                                    <span
                                                        class="history-status-badge"
                                                        :class="`history-status-${history.status || 'completed'}`"
                                                    >
                                                        {{ history.status === 'processing' ? 'Processing' : 'Completed' }}
                                                    </span>
                                                    <span class="history-date">{{ formatHistoryDate(history.downloadedAt) }}</span>
                                                    <span class="history-fields-count">{{ history.fields.length }} fields</span>
                                                </div>
                                                <div v-if="history.status === 'processing'" class="history-progress">
                                                    <span class="history-progress-label">Processing</span>
                                                    <div class="history-progress-bar">
                                                        <div
                                                            class="history-progress-fill"
                                                            :style="{ width: `${history.progress || 0}%` }"
                                                        ></div>
                                                    </div>
                                                    <span class="history-progress-percent">{{ history.progress || 0 }}%</span>
                                                </div>
                                            </div>
                                            <div class="history-actions">
                                                <el-button
                                                    type="text"
                                                    icon="el-icon-download"
                                                    class="history-download-btn"
                                                    :title="history.status === 'processing' ? 'Processing...' : 'Download'"
                                                    :disabled="history.status === 'processing'"
                                                    @click="loadAndDownloadFromBundleHistory(history)"
                                                ></el-button>
                                            </div>
                                        </div>
                                        <div class="history-expand-section">
                                            <div
                                                class="history-expand-toggle"
                                                @click="toggleBundleHistoryExpand(index)"
                                            >
                                                <i :class="bundleHistoryExpanded[index] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                                                <span>Column Order</span>
                                            </div>
                                            <div
                                                v-show="bundleHistoryExpanded[index]"
                                                class="history-fields-order"
                                            >
                                                <div class="history-order-list">
                                                    <div
                                                        v-for="(fieldKey, idx) in history.fields"
                                                        :key="idx"
                                                        class="history-order-item"
                                                    >
                                                        <span class="history-order-number">{{ idx + 1 }}.</span>
                                                        <span class="history-order-label">{{ getFieldLabel(fieldKey) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </modal>
    </div>
</template>

<script>
import Modal from '../Modal.vue';
import { downloadFileInBackgroundCleanID } from '../../helpers/downloadFile';
import { Button, Dropdown, DropdownMenu, DropdownItem, Divider, Tabs, TabPane, Input, Radio, RadioGroup, Checkbox, Select, Option } from 'element-ui';

export default {
    name: 'ReportPredictDownloadModal',
    components: {
        Modal,
        [Button.name]: Button,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Divider.name]: Divider,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Input.name]: Input,
        [Radio.name]: Radio,
        [RadioGroup.name]: RadioGroup,
        [Checkbox.name]: Checkbox,
        [Select.name]: Select,
        [Option.name]: Option
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        campaignId: {
            type: [String, Number],
            required: true
        },
        campaignName: {
            type: String,
            default: 'Campaign'
        }
    },
    data() {
        return {
            campaignFieldConfig: null,
            editFieldConfig: false,
            editFieldConfigValue: null,
            EDIT_FIELD_CONFIG_NO_CUSTOM_VALUE: '__nocustom__',
            EDIT_FIELD_CONFIG_CUSTOM_VALUE: '__custom__',
            editFieldConfigName: '',
            editFieldConfigSelectedIds: [],
            customFieldsListFromGeneral: [],
            masterFieldsForCampaign: [],
            editFieldConfigDraggedIndex: null,
            editFieldConfigDragOverIndex: null,
            editFieldConfigError: '',
            isSavingFieldConfig: false,
            reports: [
                // data baru
                {
                    id: 1,
                    start_date: "2026-02-04 10:15:30",
                    end_date: "2026-02-10 10:16:00",
                    job_status: "processing",
                    job_total: 100,
                    job_completed: 32,
                    total_leads: 1001,
                    created_at: "2026-02-04 10:15:30",
                },
                {
                    id: 2,
                    start_date: "2026-02-11 10:15:30",
                    end_date: "2026-02-17 10:16:00",
                    job_status: "processing",
                    job_total: 100,
                    job_completed: 68,
                    total_leads: 1001,
                    created_at: "2026-02-11 10:15:30",
                },
                {
                    id: 3,
                    start_date: "2026-02-18 10:15:30",
                    end_date: "2026-02-24 10:16:00",
                    job_status: "done",
                    job_total: 100,
                    job_completed: 100,
                    total_leads: 1001,
                    created_at: "2026-02-18 10:15:30",
                },                
                {
                    id: 4,
                    start_date: "2026-02-25 10:15:30",
                    end_date: "2026-02-31 10:16:00",
                    job_status: "done",
                    job_total: 100,
                    job_completed: 100,
                    total_leads: 1001,
                    created_at: "2026-02-25 10:15:30",
                },                
                // data baru
            ],
            isLoading: false,
            refreshInterval: null,
            customFields: [
                { key: 'first_name', label: 'First Name' },
                { key: 'last_name', label: 'Last Name' },
                { key: 'email_1', label: 'Email 1' },
                { key: 'email_2', label: 'Email 2' },
                { key: 'email_3', label: 'Email 3' },
                { key: 'email_4', label: 'Email 4' },
                { key: 'email_5', label: 'Email 5' },
                { key: 'email_6', label: 'Email 6' },
                { key: 'email_7', label: 'Email 7' },
                { key: 'email_8', label: 'Email 8' },
                { key: 'email_9', label: 'Email 9' },
                { key: 'email_10', label: 'Email 10' },
                { key: 'phone_1', label: 'Phone 1' },
                { key: 'phone_2', label: 'Phone 2' },
                { key: 'phone_3', label: 'Phone 3' },
                { key: 'address_line_1', label: 'Address Line 1' },
                { key: 'address_line_2', label: 'Address Line 2' },
                { key: 'full_address', label: 'Full Address' },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'zip', label: 'Zip' },
                { key: 'country', label: 'Country' },
                { key: 'latitude', label: 'Latitude' },
                { key: 'longitude', label: 'Longitude' },
                { key: 'county', label: 'County' },
                { key: 'gender', label: 'Gender' },
                { key: 'age_group', label: 'Age Group' },
                { key: 'marital_status', label: 'Marital Status' },
                { key: 'education_level', label: 'Education Level' },
                { key: 'language', label: 'Language' },
                { key: 'source_vendor', label: 'Source Vendor' }
            ],
            customFieldSelections: {},
            customFieldsModalOpen: false,
            currentCustomReport: null,
            draggedIndex: null,
            dragOverIndex: null,
            activeTab: 'configure',
            customFieldsReportName: '',
            historyExpanded: {},
            customFieldsHistoryLoading: false,
            customFieldsHistory: [
                // Dummy history for report id 3 (completed report)
                {
                    reportId: 3,
                    name: 'Weekly Report - Processing',
                    fields: ['first_name', 'last_name', 'email_1', 'email_2', 'phone_1', 'city', 'state', 'zip'],
                    downloadedAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
                    status: 'processing',
                    progress: 32
                },
                {
                    reportId: 3,
                    name: 'Weekly Report - Full Details',
                    fields: ['first_name', 'last_name', 'email_1', 'email_2', 'phone_1', 'address_line_1', 'city', 'state', 'zip', 'country', 'gender', 'age_group'],
                    downloadedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
                    status: 'completed',
                    progress: 100
                },
            ],
            bundleModalOpen: false,
            bundleActiveTab: 'configure',
            bundleDownloadType: 'csv',
            bundleCsvType: 'original',
            csvTypesList: [],
            bundleCustomFieldSelections: [],
            bundleReportName: '',
            bundleDraggedIndex: null,
            bundleDragOverIndex: null,
            bundleHistoryExpanded: {},
            selectedBundleReports: [],
            bundleDownloadHistory: [
                {
                    reportCount: 2,
                    downloadedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
                    status: 'processing',
                    fields: ['first_name', 'last_name', 'email_1', 'phone_1', 'city', 'state'],
                    name: 'Bundle Report',
                    progress: 50
                },
                {
                    reportCount: 2,
                    downloadedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
                    status: 'processing',
                    fields: ['first_name', 'last_name', 'email_1', 'phone_1', 'city', 'state'],
                    name: 'Bundle Report',
                    progress: 80
                },
                {
                    reportCount: 2,
                    downloadedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
                    status: 'completed',
                    fields: ['first_name', 'last_name', 'email_1', 'phone_1', 'city', 'state'],
                    name: 'Bundle Report',
                    progress: 100
                },
                {
                    reportCount: 2,
                    downloadedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
                    status: 'completed',
                    fields: ['first_name', 'last_name', 'email_1', 'phone_1', 'city', 'state'],
                    name: 'Bundle Report',
                    progress: 100
                },
            ]
        };
    },
    computed: {
        localOpen: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        currentCustomFieldSelections: {
            get() {
                if (!this.currentCustomReport) return [];
                return this.customFieldSelections[this.currentCustomReport.id] || [];
            },
            set(val) {
                if (!this.currentCustomReport) return;
                this.$set(this.customFieldSelections, this.currentCustomReport.id, val);
            }
        },
        filteredCustomFieldsHistory() {
            if (!this.currentCustomReport) return [];
            return this.customFieldsHistory.filter(
                history => history.reportId === this.currentCustomReport.id
            );
        },
        completedReports() {
            return this.reports.filter(r => r.job_status === 'done');
        },
        filteredBundleDownloadHistory() {
            return this.bundleDownloadHistory;
        },
        hasProcessingReports() {
            // Return false if reports array is empty
            if (!this.reports || this.reports.length === 0) {
                this.clearRefreshInterval();
                return false;
            }
            return this.reports.some(report => this.isProcessingOrPending(report.job_status));
        }
    },
    watch: {
        localOpen(newVal) {
            if (newVal) {
                this.clearRefreshInterval();
                this.fetchReports();
                this.fetchCampaignFieldConfig();
                this.fetchCsvTypes();
                this.editFieldConfig = false;
                this.campaignFieldConfig = null;
            } else {
                this.clearRefreshInterval();
                this.isLoading = false;
                this.reports = [];
                this.customFieldsModalOpen = false;
                this.currentCustomReport = null;
                this.activeTab = 'configure';
                this.editFieldConfig = false;
                this.campaignFieldConfig = null;
            }
        },
        hasProcessingReports(newVal) {
            if (newVal && this.localOpen) {
                // Start interval if there are processing reports and modal is open
                this.startRefreshInterval();
            } else {
                // Stop interval if no processing reports
                this.clearRefreshInterval();
            }
        },
        customFieldsModalOpen(newVal) {
            if (!newVal) {
                this.activeTab = 'configure';
            }
        }
    },
    methods: {
        openCustomFieldsModal(report) {
            this.currentCustomReport = report;
            this.ensureCustomFieldSelection(report);
            this.activeTab = 'configure';
            this.customFieldsReportName = '';
            this.historyExpanded = {};
            this.customFieldsModalOpen = true;
            this.customFieldsHistory = [];
        },
        onCustomFieldsTabClick(tab) {
            if (tab.name === 'history' && this.currentCustomReport) {
                this.fetchCustomFieldsHistoryForReport(this.currentCustomReport.id);
            }
        },
        async fetchCustomFieldsHistoryForReport(reportId) {
            this.customFieldsHistoryLoading = true;
            try {
                const res = await this.$store.dispatch('getPredictReportCustomFieldsHistory', reportId);
                if (res.result === 'success' && Array.isArray(res.history)) {
                    this.customFieldsHistory = res.history;
                } else {
                    this.customFieldsHistory = [];
                }
            } catch (e) {
                console.error('Failed to fetch custom fields history for report', e);
                this.customFieldsHistory = [];
            } finally {
                this.customFieldsHistoryLoading = false;
            }
        },
        ensureCustomFieldSelection(report) {
            if (!this.customFieldSelections[report.id]) {
                const allFields = this.customFields.map((field) => field.key);
                this.$set(this.customFieldSelections, report.id, allFields);
            }
        },
        getFieldLabel(fieldKey) {
            const match = this.customFields.find((field) => field.key === fieldKey);
            return match ? match.label : fieldKey;
        },
        moveCustomFieldInModal(fieldKey, direction) {
            if (!this.currentCustomReport) return;
            const list = [...this.currentCustomFieldSelections];
            const currentIndex = list.indexOf(fieldKey);
            if (currentIndex === -1) return;
            const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
            if (targetIndex < 0 || targetIndex >= list.length) return;
            const [item] = list.splice(currentIndex, 1);
            list.splice(targetIndex, 0, item);
            this.currentCustomFieldSelections = list;
        },
        selectAllCustomFieldsInModal() {
            const allFields = this.customFields.map((field) => field.key);
            this.currentCustomFieldSelections = allFields;
        },
        clearCustomFieldsInModal() {
            this.currentCustomFieldSelections = [];
        },
        downloadCustomFieldsFromModal() {
            if (!this.currentCustomReport) return;
            const selected = this.currentCustomFieldSelections;
            if (selected.length === 0) {
                this.$notify({
                    type: 'warning',
                    message: 'Please select at least one field',
                    duration: 2000
                });
                return;
            }
            
            // Save to history with processing status
            const historyEntry = {
                reportId: this.currentCustomReport.id,
                name: this.customFieldsReportName || `Custom Report ${new Date().toLocaleDateString()}`,
                fields: [...selected],
                downloadedAt: new Date().toISOString(),
                status: 'processing',
                progress: 0
            };
            this.customFieldsHistory.unshift(historyEntry);
            
            // Keep only last 10 history entries
            if (this.customFieldsHistory.length > 10) {
                this.customFieldsHistory = this.customFieldsHistory.slice(0, 10);
            }
            
            // Simulate progress
            this.simulateDownloadProgress(historyEntry);
            
            this.$notify({
                type: 'info',
                message: `Dummy custom CSV (${selected.length} fields) for report #${this.currentCustomReport.id}`,
                duration: 2500
            });
        },
        simulateDownloadProgress(historyEntry) {
            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    historyEntry.status = 'completed';
                    clearInterval(interval);
                }
                historyEntry.progress = progress;
                this.$forceUpdate();
            }, 300);
        },
        toggleHistoryExpand(index) {
            this.$set(this.historyExpanded, index, !this.historyExpanded[index]);
        },
        loadAndDownloadFromHistory(history) {
            if (!this.currentCustomReport) return;
            if (history.status === 'processing') return;
            if (history.id) {
                const appDataServerUrl = process.env.VUE_APP_DATASERVER_URL;
                const fileName = (this.campaignName || 'report') + '_' + (history.name || 'custom').replace(/\s+/g, '_') + '.zip';
                const backendUrl = `${appDataServerUrl}/configuration/predict-id/download-report-file/${history.id}`;
                downloadFileInBackgroundCleanID(backendUrl, fileName);
                return;
            }
            // Fallback: load configuration and trigger download
            this.currentCustomFieldSelections = [...(history.fields || [])];
            this.customFieldsReportName = history.name || '';
            this.activeTab = 'configure';
            this.downloadCustomFieldsFromModal();
        },
        formatHistoryDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            
            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
            if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
            if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        handleDragStart(index, event) {
            this.draggedIndex = index;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', index.toString());
            if (event.target.classList.contains('custom-fields-order-item')) {
                event.target.style.opacity = '0.5';
            } else {
                const item = event.target.closest('.custom-fields-order-item');
                if (item) item.style.opacity = '0.5';
            }
        },
        handleDragEnd(event) {
            const item = event.target.closest('.custom-fields-order-item');
            if (item) item.style.opacity = '1';
            this.draggedIndex = null;
            this.dragOverIndex = null;
        },
        handleDragOver(index) {
            if (this.draggedIndex !== null && this.draggedIndex !== index) {
                this.dragOverIndex = index;
            }
        },
        handleDragLeave() {
            this.dragOverIndex = null;
        },
        handleDrop(event) {
            event.preventDefault();
            if (this.draggedIndex === null || this.dragOverIndex === null) return;
            
            const list = [...this.currentCustomFieldSelections];
            const [item] = list.splice(this.draggedIndex, 1);
            list.splice(this.dragOverIndex, 0, item);
            this.currentCustomFieldSelections = list;
            
            this.draggedIndex = null;
            this.dragOverIndex = null;
        },
        async fetchReports(showLoading = true) {
            if (showLoading) {
                this.isLoading = true;
            }
            try {
                const response = await this.$store.dispatch('getLeadspeekPredictReports', {
                    campaignId: this.campaignId
                });
                this.reports = response.reports;
                
                // After fetching, check if all reports are done and clear interval if needed
                if (!this.hasProcessingReports) {
                    this.clearRefreshInterval();
                }
            } catch (error) {
                console.error('Failed to fetch reports:', error);
                // Only show notification if not from interval (showLoading = true)
                if (showLoading) {
                    this.$notify({type: 'danger',message: 'Failed to load reports',icon: 'fas fa-bug'});
                }
                this.reports = [];
                // Clear interval if fetch fails
                this.clearRefreshInterval();
            } finally {
                if (showLoading) {
                    this.isLoading = false;
                }
            }
        },
        async fetchCampaignFieldConfig() {
            if (!this.campaignId) return;
            try {
                const res = await this.$store.dispatch('getPredictCampaignCustomFields', this.campaignId);
                if (res.result === 'success') {
                    this.campaignFieldConfig = res.config;
                } else {
                    this.campaignFieldConfig = null;
                }
            } catch (e) {
                console.error('Failed to fetch campaign field config', e);
                this.campaignFieldConfig = null;
            }
        },
        async fetchCsvTypes() {
            try {
                const res = await this.$store.dispatch('getPredictCsvTypes');
                if (res.result === 'success' && Array.isArray(res.csvTypes)) {
                    this.csvTypesList = res.csvTypes;
                    const types = this.csvTypesList.map(c => c.csv_type);
                    if (types.length && types.indexOf(this.bundleCsvType) === -1) {
                        this.bundleCsvType = this.csvTypesList[0].csv_type;
                    }
                } else {
                    this.csvTypesList = [];
                }
            } catch (e) {
                console.error('Failed to fetch CSV types', e);
                this.csvTypesList = [];
            }
        },
        openEditFieldConfig() {
            this.editFieldConfig = true;
            this.editFieldConfigError = '';
            if (this.campaignFieldConfig && this.campaignFieldConfig.field_id) {
                this.editFieldConfigValue = this.campaignFieldConfig.field_id;
            } else if (this.campaignFieldConfig && this.campaignFieldConfig.field_name) {
                this.editFieldConfigValue = this.EDIT_FIELD_CONFIG_CUSTOM_VALUE;
            } else {
                this.editFieldConfigValue = this.EDIT_FIELD_CONFIG_NO_CUSTOM_VALUE;
            }
            this.editFieldConfigName = (this.campaignFieldConfig && this.campaignFieldConfig.field_name) ? this.campaignFieldConfig.field_name : '';
            this.editFieldConfigSelectedIds = (this.campaignFieldConfig && this.campaignFieldConfig.field_order && this.campaignFieldConfig.field_order.length)
                ? [...this.campaignFieldConfig.field_order]
                : [];
            this.fetchCustomFieldsListForCampaign();
            if (this.editFieldConfigValue === this.EDIT_FIELD_CONFIG_CUSTOM_VALUE) {
                this.fetchMasterFieldsForCampaign();
            }
            if (this.editFieldConfigValue === this.EDIT_FIELD_CONFIG_NO_CUSTOM_VALUE) {
                this.editFieldConfigName = '';
                this.editFieldConfigSelectedIds = [];
            }
            this.editFieldConfigError = '';
        },
        async fetchCustomFieldsListForCampaign() {
            try {
                const userData = this.$store.getters.userData || {};
                let companyId = null;
                if (userData.user_type === 'client') {
                    companyId = userData.company_parent;
                } else {
                    companyId = userData.company_id;
                }
                const res = await this.$store.dispatch('getPredictCustomFieldsList', { companyId, all: true });
                if (res.result === 'success' && res.customFieldsLists && res.customFieldsLists.data) {
                    this.customFieldsListFromGeneral = res.customFieldsLists.data;
                } else {
                    this.customFieldsListFromGeneral = [];
                }
            } catch (e) {
                console.error('Failed to fetch custom fields list', e);
                this.customFieldsListFromGeneral = [];
            }
        },
        async fetchMasterFieldsForCampaign() {
            try {
                const res = await this.$store.dispatch('getPredictCustomFieldsMasters');
                if (res.result === 'success' && res.masterFields) {
                    this.masterFieldsForCampaign = res.masterFields;
                } else {
                    this.masterFieldsForCampaign = [];
                }
            } catch (e) {
                console.error('Failed to fetch master fields', e);
                this.masterFieldsForCampaign = [];
            }
        },
        onEditFieldConfigSelectChange(val) {
            if (val === this.EDIT_FIELD_CONFIG_CUSTOM_VALUE) {
                this.fetchMasterFieldsForCampaign();
            }
            if (val === this.EDIT_FIELD_CONFIG_NO_CUSTOM_VALUE) {
                this.editFieldConfigName = '';
                this.editFieldConfigSelectedIds = [];
            }
            this.editFieldConfigError = '';
        },
        updateEditFieldConfigOrder() {
            this.editFieldConfigSelectedIds = this.editFieldConfigSelectedIds.filter(id =>
                this.masterFieldsForCampaign.some(f => f.id === id)
            );
        },
        getMasterFieldNameById(fieldId) {
            const f = this.masterFieldsForCampaign.find(x => x.id === fieldId);
            return f ? f.field_name : `Field #${fieldId}`;
        },
        moveEditFieldConfig(fieldId, direction) {
            const list = this.editFieldConfigSelectedIds;
            const idx = list.indexOf(fieldId);
            if (idx === -1) return;
            const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (targetIdx < 0 || targetIdx >= list.length) return;
            const arr = [...list];
            [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
            this.editFieldConfigSelectedIds = arr;
        },
        handleEditFieldConfigDragStart(index, event) {
            this.editFieldConfigDraggedIndex = index;
            event.dataTransfer.effectAllowed = 'move';
            const item = event.target.closest('.campaign-fields-order-item-inline');
            if (item) item.style.opacity = '0.5';
        },
        handleEditFieldConfigDragEnd(event) {
            const item = event.target.closest('.campaign-fields-order-item-inline');
            if (item) item.style.opacity = '1';
            this.editFieldConfigDraggedIndex = null;
            this.editFieldConfigDragOverIndex = null;
        },
        handleEditFieldConfigDragOver(index) {
            if (this.editFieldConfigDraggedIndex !== null && this.editFieldConfigDraggedIndex !== index) {
                this.editFieldConfigDragOverIndex = index;
            }
        },
        handleEditFieldConfigDragLeave() {
            this.editFieldConfigDragOverIndex = null;
        },
        handleEditFieldConfigDrop(event) {
            event.preventDefault();
            if (this.editFieldConfigDraggedIndex === null || this.editFieldConfigDragOverIndex === null) return;
            const list = [...this.editFieldConfigSelectedIds];
            const [item] = list.splice(this.editFieldConfigDraggedIndex, 1);
            list.splice(this.editFieldConfigDragOverIndex, 0, item);
            this.editFieldConfigSelectedIds = list;
            this.editFieldConfigDraggedIndex = null;
            this.editFieldConfigDragOverIndex = null;
        },
        cancelEditFieldConfig() {
            this.editFieldConfig = false;
            this.editFieldConfigError = '';
        },
        async saveEditFieldConfig() {
            const userData = this.$store.getters.userData || {};
            let companyId = null;
            if (userData.user_type === 'client') {
                companyId = userData.company_parent;
            } else {
                companyId = userData.company_id;
            }

            this.editFieldConfigError = '';
            if (this.editFieldConfigValue === this.EDIT_FIELD_CONFIG_CUSTOM_VALUE) {
                if (!this.editFieldConfigName || !String(this.editFieldConfigName).trim()) {
                    this.editFieldConfigError = 'Configuration name is required.';
                    return;
                }
                if (!this.editFieldConfigSelectedIds.length) {
                    this.editFieldConfigError = 'Select at least one field.';
                    return;
                }
            }

            const isNoCustom = this.editFieldConfigValue === this.EDIT_FIELD_CONFIG_NO_CUSTOM_VALUE;
            const isCustom = this.editFieldConfigValue === this.EDIT_FIELD_CONFIG_CUSTOM_VALUE;
            this.isSavingFieldConfig = true;

            try {
                await this.$store.dispatch('updatePredictCampaignCustomFields', {
                    leadspeekApiId: this.campaignId,
                    companyId,
                    fieldId: isNoCustom ? null : (isCustom ? null : this.editFieldConfigValue),
                    fieldName: isNoCustom ? null : (isCustom ? this.editFieldConfigName.trim() : null),
                    fieldOrder: isNoCustom ? [] : (isCustom ? this.editFieldConfigSelectedIds : [])
                });
                await this.fetchCampaignFieldConfig();
                this.editFieldConfig = false;
                const successMsg = isNoCustom
                    ? 'Custom fields cleared. Next report fetch will use default.'
                    : 'Custom fields configuration saved. Next report fetch will use this configuration.';
                this.$notify({ type: 'success', message: successMsg, icon: 'fas fa-check' });
            } catch (err) {
                console.error('Failed to save campaign field config', err);
                const msg = (err.response && err.response.data && err.response.data.message) || 'Failed to save configuration.';
                this.editFieldConfigError = msg;
                this.$notify({ type: 'danger', message: msg, icon: 'fas fa-bug' });
            } finally {
                this.isSavingFieldConfig = false;
            }
        },
        startRefreshInterval() {
            // Clear any existing interval first
            this.clearRefreshInterval();
            
            // Validate: don't start if reports array is empty
            if (!this.reports || this.reports.length === 0) {
                return;
            }
            
            // Only start interval if there are processing reports and modal is open
            console.log('start refresh interval before', this.hasProcessingReports, this.localOpen);
            if (this.hasProcessingReports && this.localOpen) {
                console.log('start refresh interval after');
                this.refreshInterval = setInterval(() => {
                    // Validate again: check if reports array is empty
                    if (!this.reports || this.reports.length === 0) {
                        this.clearRefreshInterval();
                        return;
                    }
                    
                    // Check again if there are still processing reports
                    if (this.hasProcessingReports && this.localOpen) {
                        // Fetch without showing loading indicator (silent refresh)
                        this.fetchReports(false);
                    } else {
                        // Stop interval if no more processing reports
                        this.clearRefreshInterval();
                    }
                }, 10000); // 1 minute = 60000 milliseconds
            }
        },
        clearRefreshInterval() {
            if (this.refreshInterval !== null) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },
        handleClose() {
            this.localOpen = false;
        },
        async handleDownloadType(report, type) {
            if (!report || !report.id) {
                this.$notify({
                    type: 'error',
                    message: 'Report information is missing',
                    duration: 3000
                });
                return;
            }

            if (!type) {
                type = 'original';
            }

            const typeLabel = String(type).toUpperCase();
            
            try {
                // Check if report is still processing
                if (this.isProcessingOrPending(report.job_status)) {
                    this.$notify({
                        type: 'warning',
                        message: 'Report is still processing. Please wait until it\'s completed.',
                        duration: 3000
                    });
                    return;
                }

                const appDataServerUrl = process.env.VUE_APP_DATASERVER_URL;
                const report_id = report.id;
                
                // Format filename based on csv_type
                const csvTypeLabel = type.charAt(0).toUpperCase() + type.slice(1);
                const file_name = `${this.campaignName}_report_${this.formatDate(report.start_date)}_to_${this.formatDate(report.end_date)}_${csvTypeLabel}.zip`;
                
                // Build URL with csv_type as route parameter
                const url = `${appDataServerUrl}/configuration/predict-id/download-report/${report_id}/${type}`;
                
                // Download file
                downloadFileInBackgroundCleanID(url, file_name);
                
            } catch (error) {
                console.error('Download error:', error);
                this.$notify({
                    type: 'error',
                    message: `Failed to download ${typeLabel} CSV for report #${report.id}`,
                    duration: 3000
                });
            }
        },
        handleDownload(report) {                 
            try {
                const appDataServerUrl = process.env.VUE_APP_DATASERVER_URL;
                const report_id = report.id;
                const file_name = `${this.campaignName}_report_${this.formatDate(report.start_date)}_to_${this.formatDate(report.end_date)}.csv`;
                const url = `${appDataServerUrl}/configuration/predict-id/download-report/${report_id}`;                
                downloadFileInBackgroundCleanID(url, file_name);
            } catch (error) {
                console.error('Download error:', error);
                this.$notify({type: 'error',message: 'Failed to download report',duration: 3000});
            }
        },
        formatReportTitle(report) {
            const startDate = this.formatDate(report.start_date);
            const endDate = this.formatDate(report.end_date);
            return `Weekly Report (${startDate} - ${endDate})`;
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            // Display full month name (e.g., December 17, 2025)
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        formatNumber(number) {
            if (number === null || number === undefined) return '0';
            return Number(number).toLocaleString('en-US');
        },
        getDisplayStatus(jobStatus) {
            if (!jobStatus) return '';
            if (jobStatus === 'done') return 'completed';
            if (jobStatus === 'processing' || jobStatus === 'pending') return 'processing';
            return jobStatus;
        },
        isProcessingOrPending(jobStatus) {
            return jobStatus === 'processing' || jobStatus === 'pending';
        },
        getProgressPercentage(report) {
            if (!report || !this.isProcessingOrPending(report.job_status)) return 0;
            if (!report.job_total || report.job_total === 0) return 0;
            const completed = report.job_completed || 0;
            const total = report.job_total;
            const percentage = Math.round((completed / total) * 100);
            return Math.min(100, Math.max(0, percentage));
        },
        openDownloadAllModal() {
            if (this.completedReports.length === 0) {
                this.$notify({
                    type: 'warning',
                    message: 'No completed reports available',
                    duration: 2000
                });
                return;
            }
            this.bundleActiveTab = 'configure';
            this.bundleDownloadType = 'csv';
            this.bundleCsvType = (this.csvTypesList.length > 0) ? this.csvTypesList[0].csv_type : 'original';
            this.bundleCustomFieldSelections = this.customFields.map(f => f.key);
            this.bundleReportName = '';
            this.selectedBundleReports = [];
            this.bundleModalOpen = true;
        },
        isBundleReportSelected(report) {
            return this.selectedBundleReports.some(r => r.id === report.id);
        },
        toggleBundleReportSelection(report, selected) {
            if (selected) {
                if (!this.isBundleReportSelected(report)) {
                    this.selectedBundleReports.push(report);
                }
            } else {
                this.selectedBundleReports = this.selectedBundleReports.filter(r => r.id !== report.id);
            }
        },
        handleDownloadBundle() {
            if (this.selectedBundleReports.length === 0) {
                this.$notify({
                    type: 'warning',
                    message: 'Please select at least one report',
                    duration: 2000
                });
                return;
            }

            if (this.bundleDownloadType === 'custom') {
                if (this.bundleCustomFieldSelections.length === 0) {
                    this.$notify({
                        type: 'warning',
                        message: 'Please select at least one custom field',
                        duration: 2000
                    });
                    return;
                }

                // Save to bundle history
                const bundleEntry = {
                    reportCount: this.selectedBundleReports.length,
                    downloadedAt: new Date().toISOString(),
                    status: 'processing',
                    progress: 0,
                    fields: [...this.bundleCustomFieldSelections],
                    name: this.bundleReportName || 'Bundle Report'
                };
                this.bundleDownloadHistory.unshift(bundleEntry);
                
                // Keep only last 5 bundle history entries
                if (this.bundleDownloadHistory.length > 5) {
                    this.bundleDownloadHistory = this.bundleDownloadHistory.slice(0, 5);
                }
                
                // Simulate progress
                this.simulateBundleDownloadProgress(bundleEntry);
                
                this.$notify({
                    type: 'info',
                    message: `Dummy: Download ${this.selectedBundleReports.length} reports with custom fields as bundle`,
                    duration: 2500
                });
            } else {
                // CSV Type download
                const typeLabel = this.bundleCsvType.toUpperCase();
                this.$notify({
                    type: 'info',
                    message: `Dummy: Download ${this.selectedBundleReports.length} reports as ${typeLabel} CSV bundle`,
                    duration: 2500
                });
            }
            
            this.bundleModalOpen = false;
        },
        selectAllBundleFields() {
            this.bundleCustomFieldSelections = this.customFields.map(f => f.key);
        },
        clearBundleFields() {
            this.bundleCustomFieldSelections = [];
        },
        moveBundleField(fieldKey, direction) {
            const index = this.bundleCustomFieldSelections.indexOf(fieldKey);
            if (index === -1) return;

            if (direction === 'up' && index > 0) {
                const temp = this.bundleCustomFieldSelections[index];
                this.$set(this.bundleCustomFieldSelections, index, this.bundleCustomFieldSelections[index - 1]);
                this.$set(this.bundleCustomFieldSelections, index - 1, temp);
            } else if (direction === 'down' && index < this.bundleCustomFieldSelections.length - 1) {
                const temp = this.bundleCustomFieldSelections[index];
                this.$set(this.bundleCustomFieldSelections, index, this.bundleCustomFieldSelections[index + 1]);
                this.$set(this.bundleCustomFieldSelections, index + 1, temp);
            }
        },
        handleBundleDragStart(index, event) {
            this.bundleDraggedIndex = index;
            event.dataTransfer.effectAllowed = 'move';
        },
        handleBundleDragEnd() {
            this.bundleDraggedIndex = null;
            this.bundleDragOverIndex = null;
        },
        handleBundleDragOver(index) {
            if (this.bundleDraggedIndex !== null && this.bundleDraggedIndex !== index) {
                this.bundleDragOverIndex = index;
            }
        },
        handleBundleDragLeave() {
            this.bundleDragOverIndex = null;
        },
        handleBundleDrop(event) {
            event.preventDefault();
            if (this.bundleDraggedIndex === null) return;

            const draggedIndex = this.bundleDraggedIndex;
            const dropIndex = this.bundleDragOverIndex;

            if (dropIndex !== null && draggedIndex !== dropIndex) {
                const fieldKey = this.bundleCustomFieldSelections[draggedIndex];
                this.bundleCustomFieldSelections.splice(draggedIndex, 1);
                this.bundleCustomFieldSelections.splice(dropIndex, 0, fieldKey);
            }

            this.bundleDraggedIndex = null;
            this.bundleDragOverIndex = null;
        },
        toggleBundleHistoryExpand(index) {
            this.$set(this.bundleHistoryExpanded, index, !this.bundleHistoryExpanded[index]);
        },
        loadAndDownloadFromBundleHistory(history) {
            if (history.status === 'processing') return;
            
            // Load custom fields configuration
            this.bundleCustomFieldSelections = [...history.fields];
            this.bundleReportName = history.name || '';
            this.bundleActiveTab = 'configure';
            this.bundleModalOpen = true;
            
            this.$notify({
                type: 'info',
                message: `Dummy: Loaded configuration from history`,
                duration: 2000
            });
        },
        simulateBundleDownloadProgress(bundleEntry) {
            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    bundleEntry.status = 'completed';
                    clearInterval(interval);
                } else {
                    bundleEntry.progress = progress;
                }
            }, 500);
        }
    },
    beforeDestroy() {
        // Cleanup: clear interval when component is destroyed
        this.clearRefreshInterval();
    }
};
</script>

<style scoped>
.report-predict-download-modal {
    --primary-color: #222a42;
    --border-color: #e5e7eb;
    --text-light: #6b7280;
    --text-dark: #111827;
    --success-color: #10b981;
    --danger-color: #ef4444;
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
}

.modal-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    line-height: 1;
}
.modal-close-btn i {
    font-size: 16px;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 250px;
}

.loading-container i {
    font-size: 48px;
    color: var(--text-light);
    margin-bottom: 16px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes progress-stripes {
    0% { background-position: 0 0; }
    100% { background-position: 20px 0; }
}

.loading-container p {
    font-size: 16px;
    color: var(--text-dark);
    margin: 0;
}

.no-data-container {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 70px;
}

.no-data-container p {
    font-size: 16px;
    color: var(--text-light);
    margin: 0;
    font-weight: 500;
}

.reports-container {
    padding: 0 0 20px 0;
}

/* Campaign custom fields for reports */
.campaign-fields-section {
    margin-bottom: 20px;
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
}
.campaign-fields-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.campaign-fields-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--text-dark);
}
.campaign-fields-subtitle {
    margin: 0 0 16px 0;
    font-size: 11px;
    font-weight: 400;
    font-style: italic;
    color: var(--text-dark);
}
.campaign-fields-change-btn {
    padding: 0 8px;
    font-size: 13px;
}
.campaign-fields-status {
    font-size: 13px;
    color: var(--text-dark);
    margin-top: 10px;
}
.campaign-fields-none {
    color: var(--text-light);
    font-style: italic;
}
.campaign-fields-edit {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;
}
.campaign-fields-edit-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.campaign-fields-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-dark);
}
.campaign-fields-label .required {
    color: #ef4444;
}
.campaign-fields-select {
    max-width: 320px;
}
.campaign-fields-name-input {
    max-width: 320px;
}
.campaign-fields-master-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 6px 12px;
}
.campaign-fields-master-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    cursor: pointer;
}
.campaign-fields-master-item input { cursor: pointer; }
.campaign-fields-order-empty {
    font-size: 13px;
    color: var(--text-light);
    padding: 12px;
    background: #fff;
    border: 1px dashed #e5e7eb;
    border-radius: 6px;
    text-align: center;
}
.campaign-fields-order-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 315px; /* ~6 list items visible before overflow */
    overflow-y: auto;
}
.campaign-fields-order-item-inline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    cursor: grab;
}
.campaign-fields-order-item-inline.is-dragging {
    opacity: 0.5;
    cursor: grabbing;
}
.campaign-fields-order-item-inline.is-drag-over {
    border-color: #3b82f6;
    background: #eff6ff;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}
.campaign-fields-order-item-inline .order-label-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-dark);
}
.campaign-fields-order-item-inline .drag-handle-inline {
    color: #9ca3af;
    cursor: grab;
    font-size: 16px;
}
.campaign-fields-order-item-inline .order-actions-inline {
    display: flex;
    gap: 6px;
}
.campaign-fields-error {
    font-size: 13px;
    color: #ef4444;
}
.campaign-fields-edit-actions {
    display: flex;
    gap: 10px;
    margin-top: 8px;
}

.reports-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid #cfe6ff;
    background: linear-gradient(180deg, #7abfff 0%, #58abff 100%);
    border-radius: 8px 8px 0 0;
}

.header-text {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f294d;
}

.download-all-header-btn {
    background-color: #ffffff;
    border-color: rgba(255, 255, 255, 0.3);
    color: #0f294d;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.download-all-header-btn:hover {
    background-color: #f8f9fa;
    border-color: rgba(255, 255, 255, 0.5);
    color: #0f294d;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.download-all-header-btn:active {
    background-color: #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* header filter styles removed */

.reports-list {
    border: 1px solid #e5e7eb;
    border-radius: 0 0 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 0;
    max-height: 465px;
    overflow-y: auto;
    padding: 12px 8px;
}

.report-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 12px;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 0;
    margin-bottom: 0;
    transition: all 0.2s ease;
    background-color: #ffffff;
}

.report-item:last-child {
    margin-bottom: 0;
}

.report-item:hover:not(.is-downloading) {
    background-color: #f9fafb;
}

.report-item.is-downloading {
    opacity: 0.7;
    background-color: #f3f4f6;
}

.report-item.is-processing {
    background-color: #f9fafb;
}

.report-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.report-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 14px;
}

.report-title i {
    font-size: 18px;
    color: var(--primary-color);
}

.report-dates {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.report-progress {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
}

.progress-label {
    font-size: 12px;
    color: var(--text-light);
    font-weight: 600;
}

.progress-track {
    position: relative;
    flex: 1;
    height: 6px;
    background-color: #e5e7eb;
    border-radius: 999px;
    overflow: hidden;
    min-width: 120px;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
    border-radius: 999px;
    position: relative;
    transition: width 0.3s ease;
}

.progress-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.4) 0px,
        rgba(255, 255, 255, 0.4) 10px,
        rgba(255, 255, 255, 0.2) 10px,
        rgba(255, 255, 255, 0.2) 20px
    );
    background-size: 20px 20px;
    animation: progress-stripes 1s linear infinite;
}

.progress-percent {
    font-size: 12px;
    color: var(--text-dark);
    font-weight: 600;
    min-width: 36px;
    text-align: right;
}

.date-label {
    display: flex;
    align-items: center;
    gap: 13px;
    padding-left: 2px;
    font-size: 12px;
    color: var(--text-light);
}

.date-label i {
    font-size: 14px;
}

.total-leads-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-left: 12px;
    font-size: 12px;
    color: var(--text-dark);
    font-weight: 600;
}

.total-leads-label i {
    font-size: 14px;
    color: var(--primary-color);
}

.report-status {
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 600;
    text-transform: capitalize;
}

.status-completed {
    background-color: #d1fae5;
    color: #065f46;
}

.status-processing {
    background-color: #fef3c7;
    color: #92400e;
}

.status-failed {
    background-color: #fee2e2;
    color: #991b1b;
}

.report-actions {
    margin-left: 16px;
    display: flex;
    gap: 8px;
    align-items: center;
}

.btn-download {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 600;
    padding: 10px;
    min-width: 40px;
    border: none;
    border-radius: 6px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(99, 102, 241, 0.2);
}

.btn-download:hover:not(.loading):not(:disabled) {
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
    box-shadow: 0 4px 8px rgba(99, 102, 241, 0.3);
    transform: translateY(-1px);
}

.btn-download.loading,
.btn-download:disabled {
    color: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
}

.btn-download i {
    font-size: 18px;
}

.btn-download-trigger {
    color: #000000;
    font-size: 18px;
    padding: 0;
}

.btn-custom-fields {
    color: #1f2937;
    font-size: 12px;
    padding: 0;
}

.btn-custom-fields:disabled {
    color: #9ca3af;
}

/* Custom Fields Modal */
.custom-fields-modal-content {
    display: flex;
    flex-direction: column;
    max-height: 800px;
    overflow-y: auto;
}

.custom-fields-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.custom-fields-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 2px;
}

.custom-fields-list {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px 16px;
}

.custom-field-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-dark);
    cursor: pointer;
    padding: 4px 0;
}

.custom-field-item input[type="checkbox"] {
    cursor: pointer;
}

.custom-fields-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 10px;
    margin-top: 16px;
    border-top: 1px solid #e5e7eb;
}

.custom-fields-name-input {
    display: flex;
    align-items: center;
    gap: 10px;
}

.name-input-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-dark);
    white-space: nowrap;
}

.custom-fields-action-buttons {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}

.custom-fields-order-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.custom-fields-order-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
}

.custom-fields-order-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-dark);
}

.custom-fields-empty {
    font-size: 13px;
    color: var(--text-light);
    padding: 20px;
    text-align: center;
    background-color: #f9fafb;
    border-radius: 6px;
    border: 1px dashed #e5e7eb;
}

.custom-fields-order-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 450px;
    overflow-y: auto;
    padding-right: 4px;
}

.custom-fields-order-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background-color: #ffffff;
    transition: background-color 0.2s ease;
}

.custom-fields-order-item.is-dragging {
    opacity: 0.5;
    cursor: grabbing;
}

.custom-fields-order-item.is-drag-over {
    border-color: #3b82f6;
    background-color: #eff6ff;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.custom-fields-order-item {
    cursor: grab;
}

.custom-fields-order-item:active {
    cursor: grabbing;
}

.order-label {
    font-size: 13px;
    color: var(--text-dark);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
}

.drag-handle {
    color: #9ca3af;
    cursor: grab;
    font-size: 16px;
}

.drag-handle:active {
    cursor: grabbing;
}

.order-actions {
    display: flex;
    gap: 6px;
}

.reports-list::-webkit-scrollbar {
    width: 6px;
}

.reports-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.reports-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.reports-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.custom-fields-modal-content::-webkit-scrollbar,
.custom-fields-order-list::-webkit-scrollbar {
    width: 6px;
}

.custom-fields-modal-content::-webkit-scrollbar-track,
.custom-fields-order-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.custom-fields-modal-content::-webkit-scrollbar-thumb,
.custom-fields-order-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.custom-fields-modal-content::-webkit-scrollbar-thumb:hover,
.custom-fields-order-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Custom Fields Tab Content */
.custom-fields-tab-content {
    padding: 20px;
    min-height: 90px;
    display: flex;
    flex-direction: column;
    /* gap: 20px; jangan kasih gap lagi disini */ 
}

.custom-fields-modal-content >>> .el-tabs--border-card {
    border: 1px solid #dcdfe6;
    box-shadow: none;
}

.custom-fields-modal-content >>> .el-tabs--border-card > .el-tabs__header {
    background-color: #f5f7fa;
    border-bottom: 1px solid #dcdfe6;
    margin: 0;
}

.custom-fields-modal-content >>> .el-tabs--border-card > .el-tabs__content {
    padding: 0;
}

/* Custom Fields History */
.custom-fields-history-section {
    padding: 10px 0;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.custom-fields-history-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: #909399;
    font-size: 14px;
}

.custom-fields-history-loading .el-icon-loading {
    font-size: 20px;
}

.custom-fields-history-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 730px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
}

.custom-fields-history-item {
    padding: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background-color: #ffffff;
    transition: all 0.2s ease;
}

.history-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    gap: 16px;
}

.history-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
}

.history-main-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.history-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-dark);
}

.history-status-badge {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 12px;
    text-transform: capitalize;
}

.history-status-processing {
    background-color: #fef3c7;
    color: #92400e;
}

.history-status-completed {
    background-color: #d1fae5;
    color: #065f46;
}

.history-date {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-light);
}

.history-fields-count {
    font-size: 12px;
    color: var(--text-light);
    background-color: #f3f4f6;
    padding: 2px 8px;
    border-radius: 12px;
}

.history-progress {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
}

.history-progress-label {
    font-size: 12px;
    color: var(--text-light);
    font-weight: 600;
    min-width: 70px;
}

.history-progress-bar {
    flex: 1;
    height: 6px;
    background-color: #e5e7eb;
    border-radius: 999px;
    overflow: hidden;
    min-width: 120px;
}

.history-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
    border-radius: 999px;
    transition: width 0.3s ease;
    position: relative;
}

.history-progress-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.4) 0px,
        rgba(255, 255, 255, 0.4) 10px,
        rgba(255, 255, 255, 0.2) 10px,
        rgba(255, 255, 255, 0.2) 20px
    );
    background-size: 20px 20px;
    animation: progress-stripes 1s linear infinite;
}

.history-progress-percent {
    font-size: 12px;
    color: var(--text-dark);
    font-weight: 600;
    min-width: 36px;
    text-align: right;
}

.history-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.history-download-btn {
    color: #000000;
    font-size: 18px;
    padding: 0;
    transition: opacity 0.2s ease;
}

.history-download-btn:disabled {
    color: #9ca3af;
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
}

.history-expand-section {
    margin-top: 8px;
}

.history-expand-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #3b82f6;
    cursor: pointer;
    padding: 6px 0;
    user-select: none;
    transition: color 0.2s ease;
}

.history-expand-toggle:hover {
    color: #2563eb;
}

.history-expand-toggle i {
    font-size: 14px;
}

.history-fields-order {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
}

.history-order-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 200px;
    overflow-y: auto;
    padding-right: 4px;
}

.history-order-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background-color: #f9fafb;
    border-radius: 4px;
    font-size: 12px;
}

.history-order-number {
    font-weight: 600;
    color: #6b7280;
    min-width: 24px;
}

.history-order-label {
    color: var(--text-dark);
}

.history-order-list::-webkit-scrollbar {
    width: 4px;
}

.history-order-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.history-order-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
}

.history-order-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.custom-fields-history-list::-webkit-scrollbar {
    width: 6px;
}

.custom-fields-history-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.custom-fields-history-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.custom-fields-history-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Bundle Download Sections */
.bundle-download-type-section {
    margin-bottom: 20px;
}

.bundle-csv-type-section {
    margin-bottom: 20px;
}

.bundle-radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
}

.bundle-reports-section {
    margin-top: 20px;
}

.bundle-reports-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
    max-height: 300px;
    overflow-y: auto;
}

.bundle-report-item {
    padding: 12px;
    background-color: #f9fafb;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
}

.bundle-report-item .el-checkbox {
    width: 100%;
    display: flex;
    align-items: flex-start;
}

.bundle-report-item .el-checkbox__label {
    width: 100%;
    padding-left: 8px;
}

.bundle-report-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

.bundle-report-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-dark);
}

.bundle-report-details {
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: var(--text-light);
}

.bundle-report-date,
.bundle-report-leads {
    display: flex;
    align-items: center;
    gap: 4px;
}
</style>
